import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { mockData } from '../data/mock';
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle,
  CardDescription 
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Clock, 
  User, 
  ArrowRight, 
  BookOpen, 
  Search, 
  X,
  Calendar,
  Tag,
  Share2,
  Bookmark,
  TrendingUp,
  Filter,
  ChevronDown,
  Mail,
  Eye,
  Heart,
  MessageCircle
} from 'lucide-react';

const Blog = () => {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('latest');
 const [bookmarkedPosts, setBookmarkedPosts] = useState([]);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [showFilters, setShowFilters] = useState(false);

  // Extract unique categories
  const categories = ['All', ...new Set(mockData.blogPosts.map(post => post.category))];
  
  // Get popular tags
  const popularTags = mockData.blogPosts.flatMap(post => post.tags || []).slice(0, 10);

  // Filter and sort posts
  const filteredPosts = mockData.blogPosts
    .filter(post => {
      const categoryMatch = selectedCategory === 'All' || post.category === selectedCategory;
      const searchMatch = 
        searchQuery === '' ||
        post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        post.excerpt.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (post.tags && post.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase())));
      
      return categoryMatch && searchMatch;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'latest':
          return new Date(b.date).getTime() - new Date(a.date).getTime();
        case 'popular':
          return (b.views || 0) - (a.views || 0);
        case 'reading':
          return parseInt(a.readTime) - parseInt(b.readTime);
        default:
          return 0;
      }
    });

  const featuredPost = filteredPosts[0] && selectedCategory === 'All' && searchQuery === '' 
    ? filteredPosts[0] 
    : null;

  const regularPosts = featuredPost ? filteredPosts.slice(1) : filteredPosts;

  // Handle bookmark toggle
 const toggleBookmark = (postId) => {
  setBookmarkedPosts(prev =>
    prev.includes(postId)
      ? prev.filter(id => id !== postId)
      : [...prev, postId]
  );
};


  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 }
  };

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Header */}
      <section className="relative py-24 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50/50 to-primary-lime/30" />
        <div
  className="absolute inset-0 opacity-40 pointer-events-none"
  style={{
    backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
  }}
/>
        
        <div className="container mx-auto max-w-6xl relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <Badge className="mb-6 bg-black text-white hover:bg-gray-800">
              <BookOpen className="w-4 h-4 mr-2" />
              Insights & Resources
            </Badge>
            
            <h1 className="font-serif text-5xl md:text-7xl font-bold text-black mb-6">
              Skyreti <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-cyan-500">Blog</span>
            </h1>
            
            <p className="text-xl text-gray-700 max-w-3xl mx-auto mb-12">
              Expert insights, actionable tips, and inspiring stories from the forefront of digital transformation in education and business.
            </p>

            {/* Search Bar */}
            <div className="max-w-2xl mx-auto mb-8">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search articles, topics, or keywords..."
                  className="w-full pl-12 pr-4 py-4 bg-white border border-gray-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                {searchQuery && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute right-2 top-1/2 transform -translate-y-1/2"
                    onClick={() => setSearchQuery('')}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>

            {/* Quick Stats */}
            <motion.div 
              className="flex flex-wrap justify-center gap-6 text-sm text-gray-600"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              <span className="flex items-center gap-2">
                <BookOpen className="w-4 h-4" />
                {mockData.blogPosts.length}+ Articles
              </span>
              <span className="flex items-center gap-2">
                <User className="w-4 h-4" />
                Expert Contributors
              </span>
              <span className="flex items-center gap-2">
                <TrendingUp className="w-4 h-4" />
                Industry Trends
              </span>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Filters & Controls */}
      <section className="sticky top-16 z-40 bg-white border-b border-gray-200 py-4 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
            {/* Left: Filters */}
            <div className="flex flex-wrap items-center gap-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowFilters(!showFilters)}
                className="lg:hidden"
              >
                <Filter className="w-4 h-4 mr-2" />
                Filters
                <ChevronDown className={`w-4 h-4 ml-2 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
              </Button>

              <div className={`${showFilters ? 'flex' : 'hidden'} lg:flex flex-wrap gap-2 w-full lg:w-auto`}>
                {categories.map(cat => (
                  <Button
                    key={cat}
                    variant={selectedCategory === cat ? "default" : "outline"}
                    size="sm"
                    onClick={() => {
                      setSelectedCategory(cat);
                      setShowFilters(false);
                    }}
                    className={selectedCategory === cat ? "bg-black text-white" : ""}
                  >
                    {cat}
                    {selectedCategory === cat && <X className="w-3 h-3 ml-2" />}
                  </Button>
                ))}
              </div>
            </div>

            {/* Right: Sort & View */}
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Sort by:</span>
                <select 
                  className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                >
                  <option value="latest">Latest</option>
                  <option value="popular">Most Popular</option>
                  <option value="reading">Reading Time</option>
                </select>
              </div>

              <div className="flex items-center gap-1 border border-gray-300 rounded-lg p-1">
                <Button
                  variant={viewMode === 'grid' ? "default" : "ghost"}
                  size="icon"
                  className={`h-8 w-8 ${viewMode === 'grid' ? 'bg-black text-white' : ''}`}
                  onClick={() => setViewMode('grid')}
                >
                  <div className="grid grid-cols-2 gap-0.5">
                    {[...Array(4)].map((_, i) => (
                      <div key={i} className="w-1.5 h-1.5 bg-current" />
                    ))}
                  </div>
                </Button>
                <Button
                  variant={viewMode === 'list' ? "default" : "ghost"}
                  size="icon"
                  className={`h-8 w-8 ${viewMode === 'list' ? 'bg-black text-white' : ''}`}
                  onClick={() => setViewMode('list')}
                >
                  <div className="space-y-1">
                    {[...Array(3)].map((_, i) => (
                      <div key={i} className="w-4 h-0.5 bg-current" />
                    ))}
                  </div>
                </Button>
              </div>
            </div>
          </div>

          {/* Popular Tags */}
          <AnimatePresence>
            {selectedCategory === 'All' && searchQuery === '' && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="mt-4 pt-4 border-t border-gray-100"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm text-gray-600 flex items-center gap-1">
                    <Tag className="w-3 h-3" />
                    Popular topics:
                  </span>
                  {popularTags.map(tag => (
                    <Button
                      key={tag}
                      variant="ghost"
                      size="sm"
                      className="text-xs"
                      onClick={() => setSearchQuery(tag)}
                    >
                      #{tag}
                    </Button>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>

      {/* Featured Post */}
      {featuredPost && (
        <section className="py-16 px-4 bg-gradient-to-r from-blue-50/50 to-gray-50">
          <div className="container mx-auto max-w-6xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <Badge className="bg-gradient-to-r from-blue-600 to-cyan-500 text-white mb-6">
                ✨ Featured Article
              </Badge>
              
              <Card className="bg-white border-none overflow-hidden hover:shadow-2xl transition-all duration-300 group">
                <div className="grid lg:grid-cols-2 gap-0">
                  {/* Image/Thumbnail */}
                  <div className="relative h-64 lg:h-auto min-h-[400px] overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-500/20 to-cyan-400/20" />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="text-8xl font-serif font-bold text-black opacity-10">
                        {featuredPost.title.split(' ').slice(0, 2).map(word => word[0]).join('')}
                      </div>
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
                    
                    {/* Quick Actions */}
                    <div className="absolute top-6 right-6 flex gap-2">
                      <Button
                        size="icon"
                        className="bg-white/90 backdrop-blur-sm hover:bg-white"
                        onClick={() => toggleBookmark(featuredPost.id)}
                      >
                        <Bookmark className={`w-4 h-4 ${bookmarkedPosts.includes(featuredPost.id) ? 'fill-current' : ''}`} />
                      </Button>
                      <Button
                        size="icon"
                        className="bg-white/90 backdrop-blur-sm hover:bg-white"
                      >
                        <Share2 className="w-4 h-4" />
                      </Button>
                    </div>
                    
                    {/* Category Badge */}
                    <div className="absolute bottom-6 left-6">
                      <Badge className="bg-white/90 backdrop-blur-sm text-gray-800">
                        {featuredPost.category}
                      </Badge>
                    </div>
                  </div>
                  
                  {/* Content */}
                  <div className="p-8 lg:p-12 flex flex-col justify-center">
                    <div className="flex items-center gap-4 text-sm text-gray-600 mb-4">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {featuredPost.date}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {featuredPost.readTime} read
                      </span>
                      <span className="flex items-center gap-1">
                        <Eye className="w-4 h-4" />
                        {featuredPost.views || '1.2k'} views
                      </span>
                    </div>
                    
                    <h2 className="font-serif text-3xl lg:text-4xl font-bold text-black mb-4 leading-tight">
                      {featuredPost.title}
                    </h2>
                    
                    <p className="text-gray-700 mb-6 text-lg leading-relaxed">
                      {featuredPost.excerpt}
                    </p>
                    
                    <div className="flex items-center justify-between mb-8">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                          <User className="w-5 h-5 text-gray-600" />
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">{featuredPost.author}</div>
                          <div className="text-sm text-gray-600">Expert Contributor</div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-4 text-sm text-gray-500">
                        <button className="flex items-center gap-1 hover:text-red-500">
                          <Heart className="w-4 h-4" />
                          {featuredPost.likes || '42'}
                        </button>
                        <button className="flex items-center gap-1 hover:text-blue-500">
                          <MessageCircle className="w-4 h-4" />
                          {featuredPost.comments || '12'}
                        </button>
                      </div>
                    </div>
                    
                    {/* Tags */}
                    <div className="flex flex-wrap gap-2 mb-8">
                      {featuredPost.tags?.slice(0, 3).map(tag => (
                        <Badge key={tag} variant="secondary" className="bg-gray-100 text-gray-700">
                          #{tag}
                        </Badge>
                      ))}
                    </div>
                    
                    <Button className="bg-black text-white hover:bg-gray-800 w-fit group/btn" asChild>
                      <Link to={`/blog/${featuredPost.slug}`}>
                        Read Full Article
                        <ArrowRight className="ml-2 group-hover/btn:translate-x-1 transition-transform" />
                      </Link>
                    </Button>
                  </div>
                </div>
              </Card>
            </motion.div>
          </div>
        </section>
      )}

      {/* Blog Posts Grid/List */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto max-w-6xl">
          {/* Results Info */}
          <div className="flex items-center justify-between mb-8">
            <div className="text-gray-600">
              Showing <span className="font-semibold text-gray-900">{regularPosts.length}</span> articles
              {selectedCategory !== 'All' && ` in ${selectedCategory}`}
            </div>
            <div className="text-sm text-gray-500">
              Sorted by: <span className="font-semibold text-gray-700">
                {sortBy === 'latest' ? 'Latest' : sortBy === 'popular' ? 'Most Popular' : 'Reading Time'}
              </span>
            </div>
          </div>

          <AnimatePresence mode="wait">
            {regularPosts.length === 0 ? (
              <motion.div
                key="empty"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="text-center py-20"
              >
                <Search className="w-16 h-16 text-gray-300 mx-auto mb-6" />
                <h3 className="text-2xl font-semibold text-gray-700 mb-3">
                  No articles found
                </h3>
                <p className="text-gray-600 max-w-md mx-auto mb-8">
                  {searchQuery 
                    ? `No results for "${searchQuery}". Try a different search term.`
                    : `No articles in ${selectedCategory}. Try another category.`
                  }
                </p>
                <Button
                  variant="outline"
                  onClick={() => {
                    setSelectedCategory('All');
                    setSearchQuery('');
                  }}
                >
                  View All Articles
                </Button>
              </motion.div>
            ) : viewMode === 'grid' ? (
              <motion.div
                key="grid"
                variants={containerVariants}
                initial="hidden"
                animate="show"
                className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
              >
                {regularPosts.map(post => (
                  <motion.div key={post.id} variants={itemVariants}>
                    <Card className="bg-white border-none hover:shadow-xl transition-all duration-300 overflow-hidden group h-full flex flex-col">
                      {/* Thumbnail */}
                      <div className="relative h-48 overflow-hidden">
                        <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-cyan-400/10" />
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="text-6xl font-serif font-bold text-black opacity-10">
                            {post.title.split(' ').slice(0, 2).map(word => word[0]).join('')}
                          </div>
                        </div>
                        <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                        
                        {/* Quick Actions */}
                        <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity flex gap-2">
                          <Button
                            size="icon"
                            className="bg-white/90 backdrop-blur-sm hover:bg-white"
                            onClick={() => toggleBookmark(post.id)}
                          >
                            <Bookmark className={`w-4 h-4 ${bookmarkedPosts.includes(post.id) ? 'fill-current' : ''}`} />
                          </Button>
                        </div>
                        
                        {/* Category */}
                        <div className="absolute bottom-4 left-4">
                          <Badge className="bg-white/90 backdrop-blur-sm text-gray-800">
                            {post.category}
                          </Badge>
                        </div>
                      </div>

                      {/* Content */}
                      <CardHeader className="flex-grow">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-xs text-gray-500 flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {post.date}
                          </span>
                          <span className="text-xs text-gray-500 flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {post.readTime}
                          </span>
                        </div>
                        
                        <CardTitle className="font-serif text-xl text-black group-hover:text-gray-700 transition-colors line-clamp-2">
                          {post.title}
                        </CardTitle>
                      </CardHeader>
                      
                      <CardContent>
                        <CardDescription className="text-gray-700 mb-4 line-clamp-3">
                          {post.excerpt}
                        </CardDescription>
                        
                        {/* Author & Stats */}
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center gap-2">
                            <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center">
                              <User className="w-3 h-3 text-gray-600" />
                            </div>
                            <span className="text-sm text-gray-600">{post.author}</span>
                          </div>
                          
                          <div className="flex items-center gap-3 text-xs text-gray-500">
                            <span className="flex items-center gap-1">
                              <Eye className="w-3 h-3" />
                              {post.views || '1k'}
                            </span>
                            <span className="flex items-center gap-1">
                              <Heart className="w-3 h-3" />
                              {post.likes || '24'}
                            </span>
                          </div>
                        </div>
                        
                        {/* Read Button */}
                        <Button 
                          variant="outline" 
                          className="w-full group/read"
                          asChild
                        >
                          <Link to={`/blog/${post.slug}`}>
                            <span>Read Article</span>
                            <ArrowRight className="w-4 h-4 ml-2 group-hover/read:translate-x-1 transition-transform" />
                          </Link>
                        </Button>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </motion.div>
            ) : (
              <motion.div
                key="list"
                variants={containerVariants}
                initial="hidden"
                animate="show"
                className="space-y-6"
              >
                {regularPosts.map(post => (
                  <motion.div key={post.id} variants={itemVariants}>
                    <Card className="bg-white border-none hover:shadow-lg transition-all duration-300 overflow-hidden">
                      <div className="flex flex-col md:flex-row">
                        {/* Thumbnail */}
                        <div className="md:w-48 lg:w-64 flex-shrink-0">
                          <div className="h-full min-h-[200px] md:min-h-full bg-gradient-to-br from-gray-100 to-gray-200 relative">
                            <div className="absolute inset-0 flex items-center justify-center">
                              <div className="text-6xl font-serif font-bold text-black opacity-10">
                                {post.title.split(' ').slice(0, 2).map(word => word[0]).join('')}
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        {/* Content */}
                        <div className="flex-grow p-6">
                          <div className="flex flex-col lg:flex-row lg:items-start justify-between mb-4">
                            <div>
                              <div className="flex items-center gap-3 mb-2">
                                <Badge className="bg-blue-100 text-blue-700">
                                  {post.category}
                                </Badge>
                                <span className="text-sm text-gray-500">{post.date}</span>
                              </div>
                              <CardTitle className="font-serif text-2xl text-black mb-3">
                                {post.title}
                              </CardTitle>
                              <CardDescription className="text-gray-700 mb-4">
                                {post.excerpt}
                              </CardDescription>
                            </div>
                            
                            <div className="flex items-center gap-2 mt-4 lg:mt-0">
                              <Button
                                size="icon"
                                variant="ghost"
                                onClick={() => toggleBookmark(post.id)}
                              >
                                <Bookmark className={`w-4 h-4 ${bookmarkedPosts.includes(post.id) ? 'fill-current' : ''}`} />
                              </Button>
                              <Button asChild variant="ghost" size="sm">
                                <Link to={`/blog/${post.slug}`}>
                                  Read
                                  <ArrowRight className="w-4 h-4 ml-2" />
                                </Link>
                              </Button>
                            </div>
                          </div>
                          
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                              <div className="flex items-center gap-2 text-sm text-gray-600">
                                <User className="w-4 h-4" />
                                {post.author}
                              </div>
                              <div className="flex items-center gap-4 text-sm text-gray-500">
                                <span className="flex items-center gap-1">
                                  <Clock className="w-4 h-4" />
                                  {post.readTime}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Eye className="w-4 h-4" />
                                  {post.views || '1k'}
                                </span>
                              </div>
                            </div>
                            
                            {post.tags && post.tags.length > 0 && (
                              <div className="hidden md:flex items-center gap-2">
                                {post.tags.slice(0, 2).map(tag => (
                                  <Badge key={tag} variant="secondary" className="bg-gray-100 text-gray-700 text-xs">
                                    #{tag}
                                  </Badge>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </Card>
                  </motion.div>
                ))}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Load More */}
          {regularPosts.length > 0 && (
            <div className="text-center mt-12">
              <Button variant="outline" className="px-8">
                Load More Articles
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="py-20 px-4 bg-gradient-to-br from-gray-900 to-black text-white">
        <div className="container mx-auto max-w-4xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Badge className="mb-6 bg-white/20 text-white backdrop-blur-sm border-white/30">
              <Mail className="w-4 h-4 mr-2" />
              Stay Updated
            </Badge>
            
            <h2 className="font-serif text-4xl md:text-5xl font-bold mb-6">
              Never Miss an Insight
            </h2>
            
            <p className="text-xl text-gray-300 mb-10 max-w-2xl mx-auto">
              Subscribe to our newsletter for the latest trends, expert tips, and industry insights delivered to your inbox.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
              <div className="flex-1 relative">
                <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="email"
                  placeholder="Your email address"
                  className="w-full pl-12 pr-4 py-4 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent backdrop-blur-sm"
                />
              </div>
              <Button className="bg-white text-black hover:bg-gray-200 px-8 py-4">
                Subscribe
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
            
            <p className="text-sm text-gray-400 mt-6">
              Join 10,000+ professionals who read our newsletter
            </p>
          </motion.div>
        </div>
      </section>

      {/* Bottom Navigation */}
      <section className="py-12 px-4 bg-gray-50">
        <div className="container mx-auto max-w-6xl">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <h3 className="font-semibold text-gray-900 mb-4">Explore Topics</h3>
              <div className="space-y-2">
                {categories.slice(1, 5).map(cat => (
                  <Button
                    key={cat}
                    variant="ghost"
                    className="w-full justify-start text-gray-600 hover:text-black"
                    onClick={() => setSelectedCategory(cat)}
                  >
                    {cat}
                  </Button>
                ))}
              </div>
            </div>
            
            <div>
              <h3 className="font-semibold text-gray-900 mb-4">Popular Reads</h3>
              <div className="space-y-4">
                {mockData.blogPosts.slice(0, 3).map(post => (
                  <Link 
                    key={post.id}
                    to={`/blog/${post.slug}`}
                    className="flex items-center gap-3 group"
                  >
                    <div className="w-12 h-12 bg-gray-200 rounded flex items-center justify-center flex-shrink-0">
                      <BookOpen className="w-5 h-5 text-gray-600" />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-900 group-hover:text-blue-600 line-clamp-2">
                        {post.title}
                      </div>
                      <div className="text-xs text-gray-500">{post.date}</div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
            
            <div>
              <h3 className="font-semibold text-gray-900 mb-4">Stay Connected</h3>
              <p className="text-gray-600 mb-4">
                Follow us for daily updates and insights
              </p>
              <div className="flex gap-3">
                {['Twitter', 'LinkedIn', 'YouTube'].map(platform => (
                  <Button key={platform} variant="outline" size="sm">
                    {platform}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Blog;